// Input-modality dimensions -- how the user is actually driving the app.
//
// Why this exists: PostHog's autocapture gives $os/$browser/$device_type, but
// iPadOS Safari requests desktop sites by default, so an iPad routinely reports
// as "Mac OS X" / "Desktop". Device type alone cannot tell us whether anyone is
// on a tablet, let alone whether they reached for a stylus. Without this,
// "did someone try to draw with a Pencil and give up?" is unanswerable.
//
// Both values ride as AMBIENT enrichment (like doc_kind/unit_system) via
// setTrackContext(), so they land on every subsequent event without touching
// the per-event whitelist in events.ts.
//
// Privacy: both are closed, coarse enums. No user agent string, no raw screen
// dimensions, nothing that narrows a fingerprint.

// NOTE: this module deliberately does NOT import track.ts. track.ts imports
// this one, and the repo enforces an acyclic module graph (`pnpm check:cycles`).
// The context setter is injected by initInputTracking() instead.

import { installInputLog } from '@/core/ink/inputLog';

/** Patch shape this module writes into the ambient track context. */
export type InputContextPatch = { input_mode?: string; device_class?: string };

/** How the last interaction was driven. 'pen' covers Apple Pencil and any stylus. */
export type InputMode = 'mouse' | 'touch' | 'pen';

/** Coarse device shape. Deliberately three buckets -- not a device model. */
export type DeviceClass = 'desktop' | 'tablet' | 'phone';

/**
 * Map a PointerEvent.pointerType to our closed enum. Unknown/empty pointerType
 * falls back to 'mouse' -- that is what a desktop browser reports when it does
 * not populate the field, and it keeps the enum closed.
 */
export function classifyInputMode(pointerType: string | undefined): InputMode {
    if (pointerType === 'pen') return 'pen';
    if (pointerType === 'touch') return 'touch';
    return 'mouse';
}

/**
 * Derive a coarse device class from touch capability + the physical screen's
 * long edge.
 *
 * maxTouchPoints is the reliable signal an iPad is an iPad: it survives the
 * desktop-UA default that defeats user-agent sniffing. The long edge (rather
 * than the viewport) keeps the answer stable across rotation and Split View.
 */
export function classifyDeviceClass(input: {
    maxTouchPoints: number;
    screenWidth: number;
    screenHeight: number;
}): DeviceClass {
    const { maxTouchPoints, screenWidth, screenHeight } = input;
    // A trackpad reports 0; a touchscreen reports >= 1. Some desktop displays
    // report 1 spuriously, so require genuine multi-touch.
    if (!(maxTouchPoints > 1)) return 'desktop';
    const longEdge = Math.max(screenWidth, screenHeight);
    // The smallest iPad's long edge is 1024pt; the largest phones stop well
    // short of that even in landscape.
    return longEdge >= 1024 ? 'tablet' : 'phone';
}

/** Read the current device class from the live environment. */
export function readDeviceClass(): DeviceClass | undefined {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return undefined;
    return classifyDeviceClass({
        maxTouchPoints: navigator.maxTouchPoints ?? 0,
        screenWidth: window.screen?.width ?? window.innerWidth ?? 0,
        screenHeight: window.screen?.height ?? window.innerHeight ?? 0,
    });
}

// ---- live latch --------------------------------------------------------------
//
// 'pen' is STICKY for the page load. Rationale: iPadOS suppresses finger events
// entirely while the Pencil is in use, so a session alternating between Pencil
// strokes and two-finger navigation would otherwise flap between 'pen' and
// 'touch' and split the cohort. Once a stylus has touched the glass, this is a
// stylus session.

let currentMode: InputMode | null = null;
let listenerAttached = false;
let emit: ((patch: InputContextPatch) => void) | null = null;
const subscribers = new Set<(mode: InputMode) => void>();

/**
 * Watch the latched mode. The UI uses this to reveal stylus affordances only
 * once a stylus has actually been seen -- there is no way to ask a browser
 * "is a pencil paired?" ahead of time.
 */
export function subscribeInputMode(fn: (mode: InputMode) => void): () => void {
    subscribers.add(fn);
    return () => {
        subscribers.delete(fn);
    };
}

function applyMode(next: InputMode): void {
    if (currentMode === 'pen') return; // sticky
    if (currentMode === next) return;
    currentMode = next;
    emit?.({ input_mode: next });
    for (const fn of subscribers) fn(next);
}

/** Test seam -- reset the latch between cases. */
export function resetInputModeForTests(): void {
    currentMode = null;
    listenerAttached = false;
    emit = null;
    subscribers.clear();
}

/** Current latched mode, or null if nothing has been observed yet. */
export function getInputMode(): InputMode | null {
    return currentMode;
}

/**
 * Observe a pointer event and update the ambient input_mode dimension.
 * Exported so component-level handlers can feed it directly; the window
 * listener below covers everything else.
 */
export function observePointerType(pointerType: string | undefined): void {
    applyMode(classifyInputMode(pointerType));
}

/**
 * Attach the passive window listener and stamp device_class once. Safe to call
 * more than once. Called from initTracking().
 */
export function initInputTracking(setContext: (patch: InputContextPatch) => void): void {
    if (listenerAttached) return;
    listenerAttached = true;
    emit = setContext;

    const deviceClass = readDeviceClass();
    if (deviceClass) setContext({ device_class: deviceClass });

    if (typeof window === 'undefined') return;
    // Capture phase + passive: we only read pointerType, never interfere with
    // the gesture, and we want it even when a handler stops propagation.
    window.addEventListener(
        'pointerdown',
        (e: PointerEvent) => observePointerType(e.pointerType),
        { capture: true, passive: true }
    );
    // `?inkdebug=1`: the on-device input log, the same kind of listener (capture, passive). Off: nothing installed.
    installInputLog(undefined, { inputMode: getInputMode });
}
